import { useState } from "react";
import { NavLink } from "react-router-dom";
import { UserButton } from "@clerk/react";
import { LayoutDashboard, ShoppingCart, Package, Warehouse, Truck, Receipt, ClipboardList, Tag, RefreshCw, BarChart3, Settings, Users, Menu, X, FolderTree, PackageX, TrendingDown, Leaf } from "lucide-react";
import { useApp } from "@/lib/app-context";
import { NativeSelect } from "./common";

const GROUPS = [
  { title: "Sell", items: [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, cap: "view" },
    { to: "/pos", label: "POS Checkout", icon: ShoppingCart, cap: "pos" },
    { to: "/invoices", label: "Invoices & Bills", icon: Receipt, cap: "view" },
    { to: "/orders", label: "Orders & Picking", icon: ClipboardList, cap: "orders" },
  ] },
  { title: "Catalogue & stock", items: [
    { to: "/products", label: "Products", icon: Package, cap: "view" },
    { to: "/catalogue", label: "Catalogue", icon: FolderTree, cap: "view" },
    { to: "/inventory", label: "Inventory & Racks", icon: Warehouse, cap: "view" },
    { to: "/out-of-stock", label: "Out of Stock", icon: PackageX, cap: "view" },
    { to: "/low-stock", label: "Low Stock", icon: TrendingDown, cap: "view" },
    { to: "/purchases", label: "Purchasing", icon: Truck, cap: "purchase" },
    { to: "/labels", label: "Labels & Barcodes", icon: Tag, cap: "view" },
  ] },
  { title: "Manage", items: [
    { to: "/zepio", label: "Website Sync (Zepio)", icon: RefreshCw, cap: "zepio.view" },
    { to: "/reports", label: "Reports", icon: BarChart3, cap: "reports" },
    { to: "/settings", label: "Settings", icon: Settings, cap: "inventory.edit" },
    { to: "/users", label: "Users & Audit", icon: Users, cap: "audit" },
  ] },
];

const Logo = () => (
  <div className="flex items-center gap-2 font-display text-xl font-extrabold tracking-tight text-emerald-900">
    <span className="h-8 w-8 rounded-xl bg-emerald-600 text-white flex items-center justify-center"><Leaf className="h-4 w-4" /></span>Zilabi<span className="text-amber-500 -ml-2">.</span><span className="text-sm font-semibold text-emerald-700">ERP</span>
  </div>
);

export default function Layout({ children }) {
  const { me, branches, branchId, setBranchId, can } = useApp();
  const [open, setOpen] = useState(false);
  const nav = (
    <nav className="flex flex-col gap-4 p-3">
      {GROUPS.map((g) => {
        const items = g.items.filter((n) => can(n.cap));
        if (!items.length) return null;
        return (
          <div key={g.title}>
            <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-emerald-900/40">{g.title}</div>
            {items.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"} onClick={() => setOpen(false)} data-testid={`nav-${n.to === "/" ? "dashboard" : n.to.slice(1)}`}
                className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors ${isActive ? "bg-emerald-600 text-white shadow-sm shadow-emerald-600/30" : "text-slate-600 hover:bg-emerald-50 hover:text-emerald-800"}`}>
                <n.icon className="h-4 w-4" />{n.label}
              </NavLink>
            ))}
          </div>
        );
      })}
    </nav>
  );
  return (
    <div className="min-h-screen flex bg-[#f6f8f3] grain">
      <aside className="hidden lg:flex w-64 shrink-0 flex-col bg-white border-r border-emerald-100 sticky top-0 h-screen z-40 no-print">
        <div className="px-6 py-5"><Logo /></div>
        <div className="flex-1 overflow-y-auto">{nav}</div>
        <div className="m-3 rounded-xl bg-emerald-50 px-4 py-3 text-xs text-emerald-900">Signed in as <b className="capitalize">{me?.role}</b></div>
      </aside>
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden no-print" onClick={() => setOpen(false)}>
          <div className="absolute inset-0 bg-black/30" />
          <aside className="absolute left-0 top-0 h-full w-72 bg-white overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4"><Logo /><button onClick={() => setOpen(false)} data-testid="close-mobile-nav"><X className="h-5 w-5" /></button></div>
            {nav}
          </aside>
        </div>
      )}
      <div className="flex-1 min-w-0 relative z-10">
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-emerald-100 bg-white/80 backdrop-blur-xl px-4 sm:px-6 h-14 no-print">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="open-mobile-nav"><Menu className="h-5 w-5" /></button>
          <div className="flex items-center gap-2 text-sm">
            <span className="hidden sm:inline text-muted-foreground">Branch</span>
            <NativeSelect testid="branch-switcher" value={branchId} onChange={setBranchId} className="w-44"
              options={branches.map((b) => ({ value: b.id, label: `${b.code} · ${b.name}` }))} />
          </div>
          <div className="ml-auto flex items-center gap-3">
            <span className="hidden md:block text-sm text-slate-600" data-testid="current-user-name">{me?.name}</span>
            <UserButton />
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-[1600px]">{children}</main>
      </div>
    </div>
  );
}
